<script setup lang="ts">
// eslint-disable-next-line simple-import-sort/imports
import ace from 'ace-builds';

import 'ace-builds/src-noconflict/ext-searchbox';
import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/mode-yaml';
import 'ace-builds/src-noconflict/theme-tomorrow_night_bright';

import {
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
} from 'reka-ui';
import { KaCopyButton, KaMenu } from '@/kasten-components';
import { useDialogDescriptionId, useDialogTriggerFocus } from '@/kasten-components/composables';
import { computed, nextTick, onMounted, onUnmounted, ref, toRef, watch } from 'vue';

import BusyButton from '@/components/BusyButton.vue';
import * as log from '@/logger';
import { useMainStore } from '@/stores/main';
import { copyToClipboard } from '@/utils/clipboard';
import { sleep } from '@/utils/fetch-utils';
import IconSearch from '~icons/lucide/search';
import IconClose from '~icons/lucide/x';
import KaTooltip from '@/components/tooltip/KaTooltip.vue';

import type { EditorModalProps } from './EditorModal.types';

export type { EditorModalProps };

defineOptions({ name: 'EditorModal', inheritAttrs: false });

const emit = defineEmits(['submit', 'close']);
const props = withDefaults(defineProps<EditorModalProps>(), {
  show: false,
  title: '',
  content: '',
  lang: 'yaml',
  error: '',
  backdrop: true,
  readOnly: false,
  submitButton: null,
  modal: true,
});

const hasDescription = computed(() => !!props.description);
const descriptionId = useDialogDescriptionId(hasDescription);

if (!props.description) {
  // eslint-disable-next-line no-console
  console.warn('[EditorModal] Provide a `description` prop for accessibility (aria-describedby).');
}

const mainStore = useMainStore();

const editorBody = ref<HTMLElement | null>(null);
const closeButton = ref<HTMLElement | null>(null);
const searchButton = ref<HTMLElement | null>(null);
const searchTooltip = ref<InstanceType<typeof KaTooltip> | null>(null);
const kebabMenu = ref<InstanceType<typeof KaMenu> | null>(null);
const editor = ref<ace.Ace.Editor | null>(null);
const editorBodyInitialClass = ref('');
const searchboxObserver = ref<MutationObserver | null>(null);
const searchCounterObserver = ref<MutationObserver | null>(null);
const editorResizeObserver = ref<ResizeObserver | null>(null);
const resizeRafHandle = ref<number | null>(null);
const copyButton = ref<HTMLElement | null>(null);
const copyStatus = ref<'SUCCESS' | 'ERROR' | 'INIT'>('INIT');
const searchResultsAnnouncement = ref<string>('');
const defaultCopyButtonTooltip = '<b>Copy</b> current contents to the clipboard.';
const copyButtonTooltip = computed(() => {
  switch (copyStatus.value) {
    case 'INIT':
      return defaultCopyButtonTooltip;
    case 'SUCCESS':
      return 'Copied!';
    case 'ERROR':
      return 'Failed to copy';
    default:
      return defaultCopyButtonTooltip;
  }
});

/**
 * See https://ace.c9.io/#nav=howto
 *     https://github.com/ajaxorg/ace/wiki/Configuring-Ace (config options)
 */
const initEditor = () => {
  if (!editorBody.value) return;

  editor.value = ace.edit(editorBody.value, {
    mode: `ace/mode/${props.lang}`,
    theme: 'ace/theme/tomorrow_night_bright',
    tabSize: 2,
    showPrintMargin: false,
    fontSize: 14,
    scrollPastEnd: 0.5,
    hScrollBarAlwaysVisible: false,
    enableKeyboardAccessibility: true,
    useWorker: false,
  });

  if (props.readOnly) {
    editor.value.setOptions({
      readOnly: true,
      highlightActiveLine: false,
      highlightGutterLine: false,
    });

    //@ts-ignore - this is a private method, and the only way to hide the cursor but still allow selection
    editor.value.renderer.$cursorLayer.element.style.display = 'none';
  }

  //@ts-ignore - this is a private method, and the only way to set the name of the textarea
  editor.value.textInput.getElement().setAttribute('name', 'editorTextarea');
  //@ts-ignore - this is a private method, and the only way to set the INITIAL label of the textarea, once it is focused it will be replaced with the current line number
  editor.value.textInput.getElement().setAttribute('aria-label', 'Editor textarea');

  // Observe container resize so Ace recalculates layout (fixes WCAG 1.4.10 reflow at 320px/400% zoom)
  // rAF-throttle the callback so drag-resizing the panel stays jank-free.
  if (editorBody.value) {
    editorResizeObserver.value?.disconnect();
    editorResizeObserver.value = new ResizeObserver(() => {
      if (resizeRafHandle.value !== null) cancelAnimationFrame(resizeRafHandle.value);
      resizeRafHandle.value = requestAnimationFrame(() => {
        editor.value?.resize(true);
        resizeRafHandle.value = null;
      });
    });
    editorResizeObserver.value.observe(editorBody.value);
  }

  // Attach accessibility enhancements for Ace searchbox (ext-searchbox) once editor is ready
  attachSearchboxA11y();
};

const handleClose = () => emit('close');

const handleOpenChange = (open: boolean) => {
  if (!open) handleClose();
};

// Below sm, searchButton is hidden (display:none) and can't receive focus, so
// fall back to the kebab menu trigger that replaces it on narrow viewports.
// Checked via window.innerWidth (mirrors tailwind.config.cjs's `sm: 576px`)
// rather than offsetParent/getComputedStyle — jsdom has no real layout engine
// and always reports elements as hidden, which would break this in unit tests.
const SM_BREAKPOINT_PX = 576;

const focusInitialHeaderTarget = () => {
  if (window.innerWidth < SM_BREAKPOINT_PX) {
    kebabMenu.value?.focusTrigger();
    return;
  }
  searchButton.value?.focus();
};

const handleOpenAutoFocus = (e: Event) => {
  e.preventDefault();
  focusInitialHeaderTarget();
};

// Tracks whether Search or the kebab trigger was last focused, so a resize
// that hides whichever one currently has focus can hand focus to its
// replacement rather than letting the browser drop it to <body>. Reset to
// null on any other focus target so a stale memory (eg. the user tabbed on
// to Close afterwards) can't steal focus back on a later resize.
const lastFocusedHeaderControl = ref<'search' | 'kebab' | null>(null);

const trackHeaderFocus = (e: FocusEvent) => {
  const target = e.target as HTMLElement | null;
  if (target === searchButton.value) {
    lastFocusedHeaderControl.value = 'search';
  } else if (target?.closest('[data-testid="editor-header-menu"]')) {
    lastFocusedHeaderControl.value = 'kebab';
  } else if (target && target !== document.body) {
    lastFocusedHeaderControl.value = null;
  }
};

let viewportWasNarrow = window.innerWidth < SM_BREAKPOINT_PX;

// Crossing sm can leave stale state on the side about to hide: an open kebab
// menu or a hover-opened Search tooltip, since reka only closes on
// Escape/outside-click/blur, not when the trigger vanishes via display:none.
// Also hands focus to the newly-visible control if the hiding one had it,
// checked against the crossing itself since the browser's own blur-to-body
// can lag a frame behind the resize event.
const handleWindowResize = () => {
  const isNarrow = window.innerWidth < SM_BREAKPOINT_PX;
  const crossedToWide = viewportWasNarrow && !isNarrow;
  const crossedToNarrow = !viewportWasNarrow && isNarrow;
  viewportWasNarrow = isNarrow;

  if (!isNarrow) {
    kebabMenu.value?.close();
    if (crossedToWide && lastFocusedHeaderControl.value === 'kebab') {
      searchButton.value?.focus();
    }
  } else {
    searchTooltip.value?.close();
    if (crossedToNarrow && lastFocusedHeaderControl.value === 'search') {
      kebabMenu.value?.focusTrigger();
    }
  }
};

const { handleCloseAutoFocus } = useDialogTriggerFocus(
  toRef(props, 'show'),
  toRef(props, 'triggerEl'),
);

const cleanup = () => {
  setTimeout(() => (mainStore.preventSidepanelClose = false), 100);
  if (editor.value) {
    const currentEditor = editor.value;
    const container = currentEditor.container;
    currentEditor.destroy();
    if (container) {
      container.textContent = '';
      container.removeAttribute('style');
      if (editorBodyInitialClass.value) {
        container.className = editorBodyInitialClass.value;
      }
    }
    editor.value = null;
  }
  if (searchboxObserver.value) {
    searchboxObserver.value.disconnect();
    searchboxObserver.value = null;
  }
  if (searchCounterObserver.value) {
    searchCounterObserver.value.disconnect();
    searchCounterObserver.value = null;
  }
  editorResizeObserver.value?.disconnect();
  editorResizeObserver.value = null;
  if (resizeRafHandle.value !== null) {
    cancelAnimationFrame(resizeRafHandle.value);
    resizeRafHandle.value = null;
  }
};

const onSearch = () => {
  if (editor.value) editor.value.execCommand('find');
  // The tooltip stays hover-open regardless of focus moving into Ace's
  // input — reka only clears it on pointerleave, which never fires if the
  // pointer doesn't move off the button.
  searchTooltip.value?.close();
};

const onSubmit = () => {
  if (!editor.value) return;

  emit('submit', editor.value.getValue());

  if (props.submitButton?.fn) {
    props.submitButton.fn(editor.value.getValue());
  }
};

const copyTooltipOpen = ref(false);

async function showHideTippy() {
  copyTooltipOpen.value = true;
  await sleep(1500);
  copyTooltipOpen.value = false;
  await sleep(200);
  copyStatus.value = 'INIT';
}

const onCopySuccess = async () => {
  // We want to show the 'Copied!' tooltip manually after the copy has been successful
  copyStatus.value = 'SUCCESS';

  showHideTippy();
};

const onCopyError = async () => {
  log.error('Failed to copy kubectl editor to clipboard');
  // We want to show the 'Failed to copy' tooltip manually after the copy has failed
  copyStatus.value = 'ERROR';

  showHideTippy();
};

// Screen-reader announcement for the kebab's copy action.
const menuCopyAnnouncement = ref('');

// Sighted-user feedback for the same action — a tooltip anchored to the kebab
// trigger itself (always visible, unlike the standalone desktop Copy button
// which is display:none on mobile). Deliberately a separate state from
// copyTooltipOpen/copyStatus so the two paths stay fully independent.
const menuCopyTooltipOpen = ref(false);
const menuCopyStatus = ref<'SUCCESS' | 'ERROR' | 'INIT'>('INIT');
const menuCopyTooltipContent = computed(() => {
  switch (menuCopyStatus.value) {
    case 'SUCCESS':
      return 'Copied!';
    case 'ERROR':
      return 'Failed to copy';
    default:
      return '';
  }
});

async function showHideMenuCopyTippy() {
  menuCopyTooltipOpen.value = true;
  await sleep(1500);
  menuCopyTooltipOpen.value = false;
  await sleep(200);
  menuCopyStatus.value = 'INIT';
}

const onMenuCopy = async () => {
  // Clear first so a repeat copy re-announces even when the message text is
  // identical — some screen readers ignore an aria-live update that doesn't
  // change the text content (same pattern as KaCopyButton's own alert).
  menuCopyAnnouncement.value = '';
  await nextTick();

  try {
    const value = editor.value?.getValue() || '';
    await copyToClipboard(value);
    menuCopyAnnouncement.value = 'Copied to clipboard';
    menuCopyStatus.value = 'SUCCESS';
    showHideMenuCopyTippy();
  } catch {
    menuCopyAnnouncement.value = 'Failed to copy';
    menuCopyStatus.value = 'ERROR';
    showHideMenuCopyTippy();
  }
};

// Hoisted so KaMenu receives a stable array reference instead of a new one
// (and new item objects) on every EditorModal re-render.
const kebabMenuItems = [
  { label: 'Search', onClick: onSearch },
  { label: 'Copy to clipboard', onClick: onMenuCopy },
];

const findAndFold = (text: string) => {
  if (!editor.value) return;
  if (!editor.value.getValue().includes(text)) return;

  // finds and moves the cursor to the text
  editor.value.find(text, {
    backwards: false,
    wrap: false,
    caseSensitive: true,
    wholeWord: true,
    regExp: false,
  });

  // collapse the section that contains the text we found at the current cursor location
  editor.value.session.toggleFoldWidget(true);

  // navigate back to the top of the editor
  editor.value.gotoLine(1, 0, true);
};

watch(
  () => props.lang,
  () => {
    if (!props.show) return;
    initEditor();
  },
);

watch(
  () => props.show,
  (newVal) => {
    if (newVal) {
      mainStore.preventSidepanelClose = true;
      // Actual initialization happens when editorBody mounts (see watcher below)
    } else {
      cleanup();
    }
  },
);

// Initialize Ace when the editor DOM element is actually available and dialog is shown
watch(
  editorBody,
  async (el) => {
    if (el && !editorBodyInitialClass.value) {
      editorBodyInitialClass.value = el.className;
    }
    if (el && props.show) {
      if (!editor.value) {
        // Give the DOM one more tick to fully mount within transitions
        await nextTick();
        initEditor();
      }
      if (editor.value) {
        editor.value.session.setValue(props.content);
        findAndFold('managedFields:');
        // Focus the search button (or its kebab-menu stand-in below sm) as the
        // initial focus target after Ace is ready. Reka UI's openAutoFocus is
        // suppressed (see @open-auto-focus.prevent on DialogContent) so we own
        // focus management explicitly — consistent across browser and jsdom.
        focusInitialHeaderTarget();
      }
    }
  },
  { immediate: false },
);

onMounted(() => {
  window.addEventListener('resize', handleWindowResize);
  // 'focusin' (not 'focus') so it bubbles from the actual focused element up
  // to document, letting us track focus without wiring listeners through
  // KaMenu's internal trigger button.
  document.addEventListener('focusin', trackHeaderFocus);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleWindowResize);
  document.removeEventListener('focusin', trackHeaderFocus);
  if (props.show) cleanup();
});

/**
 * Accessibility: Enhance Ace ext-searchbox to support full keyboard navigation
 * - Make non-focusable controls focusable (add tabindex and role)
 * - Provide custom keydown handlers for Tab/Shift+Tab to move focus
 * - Activate focused controls with Enter/Space
 * This works by observing for the searchbox element and patching it when it appears.
 */
function attachSearchboxA11y() {
  if (!editor.value) return;
  const container = editor.value.container;

  // Helper to patch a found searchbox element
  const enhanceSearchbox = (sb: HTMLElement) => {
    if (sb.getAttribute('data-a11y-enhanced') === 'true') {
      return; // avoid running redundantly
    }
    sb.setAttribute('data-a11y-enhanced', 'true');
    sb.setAttribute('role', 'search');

    // Mark interactive spans as focusable buttons
    const buttonActions = [
      'findPrev',
      'findNext',
      'findAll',
      'replaceAndFindNext',
      'replaceAll',
      'toggleReplace',
      'toggleRegexpMode',
      'toggleCaseSensitive',
      'toggleWholeWords',
      'searchInSelection',
    ] as const;
    type ButtonAction = (typeof buttonActions)[number];

    // Mark interactive spans as focusable buttons
    const buttonSelectors = [
      '.ace_searchbtn_close',
      ...buttonActions.map((action) => `[action="${action}"]`),
    ];

    const ariaLabels: Record<
      `[action="${ButtonAction}"]` | (string & Record<never, never>),
      string
    > = {
      '.ace_searchbtn_close': 'Close search',
      '[action="findPrev"]': 'Find previous',
      '[action="findNext"]': 'Find next',
      '[action="findAll"]': 'Find all',
      '[action="replaceAndFindNext"]': 'Replace and find next',
      '[action="replaceAll"]': 'Replace all',
      '[action="toggleReplace"]': 'Toggle replace mode',
      '[action="toggleRegexpMode"]': 'Toggle regular expression search',
      '[action="toggleCaseSensitive"]': 'Toggle case sensitive search',
      '[action="toggleWholeWords"]': 'Toggle whole word search',
      '[action="searchInSelection"]': 'Toggle search in selection',
    };

    const makeFocusable = (el: Element | null, selector?: string) => {
      if (!el) return;
      const e = el;
      if (!e.hasAttribute('tabindex')) e.setAttribute('tabindex', '0');
      if (!e.hasAttribute('role')) e.setAttribute('role', 'button');
      // Ensure accessible name
      if (!e.getAttribute('aria-label')) {
        const title =
          (selector ? ariaLabels[selector] : undefined) ||
          e.getAttribute('title') ||
          e.textContent?.trim() ||
          'button';
        e.setAttribute('aria-label', title);
      }
      // Stamp a data-testid derived from the action name for test queries
      if (!e.hasAttribute('data-testid')) {
        const action = selector?.match(/\[action="(.+)"\]/)?.[1] ?? 'close';
        e.setAttribute('data-testid', `ace-sb-${action}`);
      }
    };
    buttonSelectors.forEach((sel) => makeFocusable(sb.querySelector(sel), sel));

    // Build an ordered list of focusable controls each time (reflects replace toggle visibility)
    const getFocusables = (): HTMLElement[] => {
      const list: HTMLElement[] = [];
      const searchInput = sb.querySelector('.ace_search_form .ace_search_field');
      const replaceBox = sb.querySelector('.ace_replace_form');
      const replaceVisible = !!replaceBox && getComputedStyle(replaceBox).display !== 'none';
      const isVisible = (el: HTMLElement | null) => {
        if (!el) return false;
        const style = getComputedStyle(el);
        return style.display !== 'none' && style.visibility !== 'hidden';
      };

      if (searchInput instanceof HTMLElement) list.push(searchInput);
      ['findPrev', 'findNext', 'findAll'].forEach((action) => {
        const el = sb.querySelector(`[action="${action}"]`);
        if (el instanceof HTMLElement) list.push(el);
      });
      if (replaceVisible) {
        const replaceInput = sb.querySelector('.ace_replace_form .ace_search_field');
        if (replaceInput instanceof HTMLElement) list.push(replaceInput);
        ['replaceAndFindNext', 'replaceAll'].forEach((action) => {
          const el = sb.querySelector(`[action="${action}"]`);
          if (el instanceof HTMLElement) list.push(el);
        });
      }
      // Options row
      [
        'toggleReplace',
        'toggleRegexpMode',
        'toggleCaseSensitive',
        'toggleWholeWords',
        'searchInSelection',
      ].forEach((action) => {
        const el = sb.querySelector(`[action="${action}"]`);
        if (el instanceof HTMLElement && isVisible(el)) list.push(el);
      });
      const closeBtn = sb.querySelector('.ace_searchbtn_close');
      if (closeBtn instanceof HTMLElement) list.push(closeBtn);
      return list.filter(Boolean) as HTMLElement[];
    };

    // Normalize target to the nearest actionable element in the searchbox
    const resolveTarget = (target: EventTarget | null): HTMLElement | null => {
      if (!target || !(target as HTMLElement).closest) return null;
      const el = (target as HTMLElement).closest(
        '.ace_search_field, .ace_searchbtn, .ace_button, .ace_searchbtn_close',
      ) as HTMLElement | null;
      return el;
    };

    // Keyboard navigation handler (capture to win over Ace's internal Tab handler)
    const onKeydown = (e: KeyboardEvent) => {
      const tgt = resolveTarget(e.target);
      if (!tgt) return;

      const focusables = getFocusables();
      const idx = focusables.indexOf(tgt);
      if (idx === -1) return;

      const isInput = tgt.classList.contains('ace_search_field');
      const move = (delta: number) => {
        const nextIdx = (idx + delta + focusables.length) % focusables.length;
        const next = focusables[nextIdx];
        if (next) {
          e.preventDefault();
          e.stopPropagation();
          next.focus();
          if (next.classList.contains('ace_search_field')) {
            (next as HTMLInputElement).select?.();
          }
        }
      };

      switch (e.key) {
        case 'Tab': {
          // Let Shift+Tab go backwards
          e.preventDefault();
          e.stopPropagation();
          move(e.shiftKey ? -1 : 1);
          break;
        }
        case 'Enter':
        case ' ': {
          // Activate buttons; for inputs let Ace handle Enter normally
          if (!isInput) {
            e.preventDefault();
            e.stopPropagation();
            tgt.click();
          }
          break;
        }
        case 'Escape': {
          // Ace moves focus into the editor asynchronously, so wait for the
          // actual event rather than guessing a delay. Find All triggers the
          // same move but doesn't go through this keydown path, so it's untouched.
          //@ts-ignore - textInput is a private Ace API, same pattern as initEditor's use of it
          const editorTextArea = editor.value?.textInput.getElement();
          if (editorTextArea) {
            const redirectFocus = () => {
              editorTextArea.removeEventListener('focus', redirectFocus);
              focusInitialHeaderTarget();
            };
            editorTextArea.addEventListener('focus', redirectFocus, { once: true });
            // Cleanup in case the editor never regains focus (e.g. user clicked away).
            setTimeout(() => editorTextArea.removeEventListener('focus', redirectFocus), 500);
          }
          break;
        }
      }
    };

    // Attach with capture to pre-empt Ace's own key handler
    sb.addEventListener('keydown', onKeydown, { capture: true });

    // Observe search counter changes for accessibility announcements
    const searchCounter = sb.querySelector('.ace_search_counter');
    if (searchCounter) {
      searchCounterObserver.value?.disconnect();
      searchCounterObserver.value = new MutationObserver(() => {
        const counterText = searchCounter.textContent?.trim();
        if (counterText) {
          searchResultsAnnouncement.value = counterText;
        }
      });
      searchCounterObserver.value.observe(searchCounter, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }

    // Store a weak reference for cleanup if needed
    // When the searchbox is removed, the listener will be GC'd; no explicit detach required
  };

  // If searchbox already present (opened via Cmd+F before we attached), patch immediately
  const existing = container.querySelector('.ace_search');
  if (existing instanceof HTMLElement) enhanceSearchbox(existing);

  // Observe for future creation of the searchbox
  searchboxObserver.value?.disconnect();
  searchboxObserver.value = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((n) => {
        if (!(n instanceof HTMLElement)) return;
        // Only process nodes that are likely to contain the searchbox
        if (n.matches?.('.ace_search')) {
          enhanceSearchbox(n);
        } else if (
          n.classList?.contains('ace_search') ||
          n.classList?.contains('ace_layer') ||
          n.classList?.contains('ace_editor')
        ) {
          const sb = n.querySelector?.('.ace_search');
          if (sb instanceof HTMLElement) {
            enhanceSearchbox(sb);
          }
        }
      });
    }
  });
  searchboxObserver.value.observe(container, { childList: true, subtree: true });
}
</script>

<template>
  <DialogRoot :open="props.show" :modal="props.modal" @update:open="handleOpenChange">
    <DialogPortal>
      <Transition
        v-if="props.backdrop"
        enter-active-class="ease-in-out duration-500"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="ease-in-out duration-500"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <DialogOverlay
          v-if="props.show"
          class="fixed inset-0 z-[2100] bg-black/50 backdrop-blur-sm transition-opacity"
        />
      </Transition>

      <div
        class="fixed inset-0 z-[2100] overflow-y-auto"
        :class="{ 'pointer-events-none': !props.show }"
      >
        <div class="flex min-h-full items-center justify-center p-4 sm:p-0">
          <Transition
            enter-active-class="ease-out duration-300"
            enter-from-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to-class="opacity-100 translate-y-0 sm:scale-100"
            leave-active-class="ease-in duration-200"
            leave-from-class="opacity-100 translate-y-0 sm:scale-100"
            leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogContent
              v-if="props.show"
              v-bind="$attrs"
              :aria-modal="props.modal ? 'true' : undefined"
              :aria-describedby="descriptionId"
              class="editor relative flex h-[95vh] w-full max-w-none transform resize flex-col justify-between overflow-auto bg-[#0f1a2f] text-[#b4bfc7] sm:max-h-[80rem] sm:w-[45rem]"
              @open-auto-focus="handleOpenAutoFocus"
              @close-auto-focus="handleCloseAutoFocus"
            >
              <DialogDescription v-if="descriptionId" :id="descriptionId" class="sr-only">
                {{ props.description }}
              </DialogDescription>
              <!-- Editor Header -->
              <div
                data-testid="editor-header"
                class="flex items-start justify-between bg-white/10 px-4 py-2 text-[#b4bfc7] sm:px-6"
              >
                <!-- Below sm, the kebab sits right next to the title (design ask: keep it
                     "close to the header" title, not grouped with Close) — self-center so a
                     short title still centers in the row, but centering a no-op once a wrapped
                     title is already the row's tallest element, so the kebab still lines up
                     with the first line per Keionne's original ask (items-start below). -->
                <div class="flex min-w-0 items-start gap-2 self-center">
                  <DialogTitle
                    as="h2"
                    class="m-0 min-w-0 self-center break-words text-sm leading-[1.8] sm:truncate sm:text-xl"
                    data-testid="editor-header-title"
                    :title="props.title"
                  >
                    {{ props.title }}
                  </DialogTitle>

                  <div class="relative flex items-center sm:hidden">
                    <!-- Ace's own search box hardcodes z-index: 99 (ace_search); the default
                         z-20 here would let it render over this menu's close animation. -->
                    <KaMenu
                      ref="kebabMenu"
                      label="More actions"
                      :hide-label="true"
                      button-classes="!p-1.5 text-gray-400"
                      trigger-icon-wrapper-classes="bg-white/10 text-gray-400 hover:bg-white/20 dark:bg-white/10 dark:text-gray-400 dark:hover:bg-white/20"
                      items-classes="border-0 bg-[#293142] text-[#adb5bd] ring-0 [&_button:hover]:bg-white/10"
                      items-z-index-class="z-[100]"
                      :items="kebabMenuItems"
                      data-testid="editor-header-menu"
                    />
                    <!-- A plain absolutely-positioned bubble rather than KaTooltip: KaMenu
                         already uses asChild for its own dropdown trigger, and nesting
                         KaTooltip's asChild around it fights over the same leaf button and
                         never mounts. This just needs a fixed-position indicator, not
                         Floating UI's edge-avoidance smarts. -->
                    <div
                      v-if="menuCopyTooltipOpen"
                      role="status"
                      class="absolute left-1/2 top-full z-[100] mt-2 -translate-x-1/2 whitespace-nowrap rounded border border-gray-200 bg-white px-4 py-2 text-sm font-bold text-gray-900 shadow-sm dark:border-navy-600 dark:bg-navy-800 dark:text-gray-200"
                      data-testid="editor-menu-copy-tooltip"
                    >
                      {{ menuCopyTooltipContent }}
                    </div>
                  </div>
                </div>

                <div class="header-buttons flex gap-4" data-testid="editor-header-buttons">
                  <span
                    role="alert"
                    aria-live="assertive"
                    aria-atomic="true"
                    class="sr-only"
                    data-testid="editor-menu-copy-announcement"
                  >
                    {{ menuCopyAnnouncement }}
                  </span>

                  <KaTooltip
                    ref="searchTooltip"
                    content="Search (Cmd+F)"
                    data-testid="search-tooltip"
                    :trigger-props="{ asChild: true }"
                  >
                    <template #trigger>
                      <button
                        type="button"
                        class="btn hidden items-center justify-center rounded text-xl text-gray-400 hover:border-gray-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400 sm:inline-flex"
                        @click="onSearch"
                        aria-label="Search"
                        data-testid="editor-search-button"
                        ref="searchButton"
                      >
                        <IconSearch aria-hidden="true" class="m-0 size-5" />
                      </button>
                    </template>
                  </KaTooltip>
                  <KaTooltip
                    :trigger-props="{ asChild: true }"
                    :root-props="{ delayDuration: 200, open: copyTooltipOpen }"
                  >
                    <template #trigger>
                      <KaCopyButton
                        class="btn hidden h-full w-full border-transparent bg-transparent px-3 py-[6px] text-xl text-gray-400 hover:border-gray-400 hover:bg-transparent dark:border-transparent dark:bg-transparent dark:hover:border-gray-400 dark:hover:bg-transparent sm:inline-flex [&_svg]:h-full [&_svg]:w-full"
                        ref="copyButton"
                        :command="editor?.getValue() || ''"
                        :title="undefined"
                        @copy:success="onCopySuccess"
                        @copy:error="onCopyError"
                        label="Copy to clipboard"
                        :hide-label="true"
                        data-testid="editor-copy-button"
                      />
                    </template>
                    <template #tooltip>
                      <span class="font-bold">{{ copyButtonTooltip }}</span>
                    </template>
                  </KaTooltip>
                  <button
                    type="button"
                    class="btn inline-flex items-center justify-center rounded text-xl text-gray-400 hover:border-gray-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400"
                    @click="handleClose"
                    aria-label="Close editor"
                    data-testid="editorClose"
                    ref="closeButton"
                  >
                    <IconClose aria-hidden="true" class="m-0 size-7" />
                  </button>
                </div>
              </div>

              <!-- Editor Body -->
              <div ref="editorBody" id="editor-body" data-testid="editor-body" class="h-full">
                {{ props.content }}
              </div>

              <!-- ARIA live region for search results announcements -->
              <div
                role="status"
                aria-live="polite"
                aria-atomic="true"
                class="sr-only"
                data-testid="search-results-announcement"
              >
                {{ searchResultsAnnouncement }}
              </div>

              <!-- Error Message -->
              <div
                data-testid="editor-error-message"
                role="alert"
                :class="[
                  'error-message h-0 text-center text-[0.85rem] font-semibold text-white opacity-0 transition-all duration-200',
                  { 'h-auto px-8 py-[0.3rem] opacity-100': !!props.error && !props.readOnly },
                ]"
              >
                {{ props.error }}
              </div>

              <!-- Editor Footer -->
              <div v-if="props.submitButton" class="bg-white/10 p-4 text-center">
                <!-- TODO: Fix the cls prop to just use class -->
                <BusyButton
                  v-if="!props.readOnly"
                  cls="btn btn-outline-secondary submit-button mr-4 focus:text-gray-900"
                  :busy="!!props.submitButton.busy"
                  :busyText="props.submitButton.busyText || 'Processing...'"
                  @click="onSubmit"
                  data-testid="editor-submit-button"
                >
                  <template #content>{{ props.submitButton.text }}</template>
                </BusyButton>
                <button
                  data-testid="editor-cancel-button"
                  type="button"
                  class="btn btn-outline-secondary btn-cancel border-transparent text-[#b4bfc7] hover:border-white hover:bg-transparent focus:border-white focus:bg-transparent"
                  @click="handleClose"
                >
                  Cancel
                </button>
              </div>
            </DialogContent>
          </Transition>
        </div>
      </div>
    </DialogPortal>
  </DialogRoot>
</template>

<style lang="scss" scoped>
.editor {
  .error-message {
    background-color: desaturate(darken($warning, 10%), 10%);
  }
} /* editor */

:deep(.editor) {
  // Ace Editor Overrides
  .ace_search {
    font-family: $font-family-monospace;
    color: $gray-400;
    background: #293142;
    border: 0;
    // Shrink-to-fit content (.ace_search_field's 17em min-width + controls)
    // can exceed the available width and grow past the editor's left edge —
    // capping it wraps the controls internally instead (K10-37913).
    max-width: 100%;

    &.right {
      border-radius: 0;
    }
  }

  .ace_search_counter {
    font-family: $font-family-monospace;
  }

  .ace_search_field {
    @apply border-0 bg-navy-900 text-white #{!important};
  }

  .ace_button {
    font-size: 0.7rem;
    padding: 2px 4px;
    color: white;
    cursor: pointer;

    &:hover {
      background-color: $color-lightened-100;
    }
  }

  .ace_searchbtn {
    border: 0;
    background: unset;
    color: $gray-400;

    &:hover {
      background-color: $color-lightened-100;
      color: white;
      opacity: 1;
    }
  }

  .ace_searchbtn_close {
    &:hover {
      background-color: $color-lightened-100;
    }
  }

  .ace_search_form.ace_nomatch {
    outline: 1px solid $danger;
  }
}
</style>
